import _ from 'lodash';

import { NotificationTheme } from '../../../constants/notification';
import { NotificationsTranslationKeys } from '../../../containers/notifications/notifications-keys';
import { Dispatch, GetState } from '../../../redux/redux.types';
import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';
import { teamMembersService } from '../../../transport/services/team-members-service';
import { errorLogger } from '../../../utils/error-logger/error-logger';
import { BI_ERRORS } from '../../../utils/statistics/bi-error-logger/bi-errors';

import {
  closeTeamMemberDeleteModal,
  closeTeamMemberEditModal,
} from '../team.actions';
import {
  getDeletingTeamMember,
  getDeletingTeamMemberId,
  isTeamMemberEditModalOpened,
} from '../team.selectors-ui';
import { TeamMemberStatus } from '../team.types';

export const TEAM_MEMBERS_LIST_ACTION_NAMES = {
  DELETE_TEAM_MEMBER_SUCCESS:
    'TEAM_MEMBERS_LIST_ACTION_NAMES__DELETE_TEAM_MEMBER_SUCCESS',
  DELETE_TEAM_MEMBER_ERROR:
    'TEAM_MEMBERS_LIST_ACTION_NAMES__DELETE_TEAM_MEMBER_ERROR',

  DELETE_TEAM_INVITATION_SUCCESS:
    'TEAM_MEMBERS_LIST_ACTION_NAMES__DELETE_TEAM_INVITATION_SUCCESS',
  DELETE_TEAM_INVITATION_ERROR:
    'TEAM_MEMBERS_LIST_ACTION_NAMES__DELETE_TEAM_INVITATION_ERROR',
};

const teamMemberDeleteSuccess = (memberId: string) => ({
  type: TEAM_MEMBERS_LIST_ACTION_NAMES.DELETE_TEAM_MEMBER_SUCCESS,
  payload: memberId,
  notification: {
    key: NotificationsTranslationKeys.teamMemberDeleteSuccess,
    theme: NotificationTheme.Success,
  },
});

export const teamMemberDeleteError = (reason) => ({
  type: TEAM_MEMBERS_LIST_ACTION_NAMES.DELETE_TEAM_MEMBER_ERROR,
  payload: reason,
  notification: {
    key: NotificationsTranslationKeys.teamMemberDeleteError,
    theme: NotificationTheme.Error,
  },
});

const teamInvitationDeleteSuccess = (memberId: string) => ({
  type: TEAM_MEMBERS_LIST_ACTION_NAMES.DELETE_TEAM_INVITATION_SUCCESS,
  payload: memberId,
  notification: {
    key: NotificationsTranslationKeys.teamInvitationDeleteSuccess,
    theme: NotificationTheme.Success,
  },
});

export const teamInvitationDeleteError = (reason) => ({
  type: TEAM_MEMBERS_LIST_ACTION_NAMES.DELETE_TEAM_INVITATION_ERROR,
  payload: reason,
  notification: {
    key: NotificationsTranslationKeys.teamInvitationDeleteError,
    theme: NotificationTheme.Error,
  },
});

export const deleteTeamMemberById =
  (memberId: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const organizationId = getSelectedOrganizationId(state);

    return teamMembersService
      .deleteTeamMember({ memberId, organizationId })
      .then(
        () => dispatch(teamMemberDeleteSuccess(memberId)),
        ({ response }) => {
          dispatch(teamMemberDeleteError(_.get(response, 'data')));

          errorLogger.logError({
            errorDescription: BI_ERRORS.teamMembers.deleteTeamMemberFailed,
            otherParams: { response },
          });

          return Promise.reject(response);
        },
      );
  };

export const deleteTeamInvitationById =
  (memberId: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const organizationId = getSelectedOrganizationId(state);

    return teamMembersService
      .deleteTeamInvitation({ memberId, organizationId })
      .then(
        () => dispatch(teamInvitationDeleteSuccess(memberId)),
        ({ response }) => {
          dispatch(teamInvitationDeleteError(_.get(response, 'data')));

          errorLogger.logError({
            errorDescription: BI_ERRORS.teamMembers.deleteTeamInvitationsFailed,
            otherParams: { response },
          });

          return Promise.reject(response);
        },
      );
  };

export const noTeamMemberIdError = 'No team member id';

export const deleteTeamMember =
  () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const memberId = getDeletingTeamMemberId(state);
    const member = getDeletingTeamMember(state);

    if (!memberId) {
      dispatch(teamMemberDeleteError(noTeamMemberIdError));
      throw new Error(noTeamMemberIdError);
    }

    dispatch(closeTeamMemberDeleteModal());

    // close edit modal if delete modal opened from edit modal
    if (isTeamMemberEditModalOpened(state)) {
      dispatch(closeTeamMemberEditModal());
    }

    if (_.get(member, 'status') === TeamMemberStatus.Pending) {
      dispatch(deleteTeamInvitationById(memberId));
      return;
    }

    dispatch(deleteTeamMemberById(memberId));
  };
